import React, { useMemo, useState } from 'react'
import { Check, X, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { useEditorStore } from '../../store/editorStore'
import { previewRowExtension, type RowExtensionPlan } from '../../utils/rowExtension.logic'

const FIELD: React.CSSProperties = {
  background: 'var(--bg-panel, #111827)', border: '1px solid var(--border-color, #374151)',
  borderRadius: 6, color: 'var(--text-primary, #f3f4f6)', padding: '6px 10px', fontSize: 12,
  width: '100%', boxSizing: 'border-box',
}

export const RowExtensionModal: React.FC<{ rowId: string; onClose: () => void }> = ({ rowId, onClose }) => {
  const { row, racks, dcSettings, applyRowExtension } = useEditorStore(useShallow(state => ({
    row: state.rows[rowId], racks: state.racks, dcSettings: state.dcSettings, applyRowExtension: state.applyRowExtension,
  })))
  const [side, setSide] = useState<'head' | 'tail'>('tail')
  const [count, setCount] = useState(1)
  const plan = useMemo<RowExtensionPlan | null>(() => {
    if (!row) return null
    return previewRowExtension({
      row,
      racks: Object.values(racks).filter(rack => rack.rowId === rowId),
      allRacks: Object.values(racks),
      side,
      count,
      namingPattern: dcSettings.namingMask,
      module: dcSettings.module,
      hall: dcSettings.hall,
      namingProfiles: dcSettings.namingProfiles,
      namingAssignments: dcSettings.namingAssignments,
      floorPlan: dcSettings.floorPlan,
    })
  }, [row, racks, rowId, side, count, dcSettings])

  if (!row || !plan) return null
  const apply = () => {
    if (!plan.canApply) return
    applyRowExtension({ row: plan.nextRow, additions: plan.additions, updates: plan.updates })
    onClose()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 480, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto', background: '#0d1117', border: '1px solid #1e293b', borderRadius: 10, boxShadow: '0 24px 64px rgba(0,0,0,.8)' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#f3f4f6' }}>Продлить ряд «{row.name}»</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Предпросмотр не меняет черновик до применения</div>
          </div>
          <button onClick={onClose} aria-label="Закрыть" style={{ background: 'none', border: 0, color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 16, display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={() => setSide('tail')} style={{ ...FIELD, borderColor: side === 'tail' ? '#38bdf8' : '#374151', color: side === 'tail' ? '#7dd3fc' : '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><ArrowDownToLine size={14} /> Хвост</button>
            <button onClick={() => setSide('head')} style={{ ...FIELD, borderColor: side === 'head' ? '#38bdf8' : '#374151', color: side === 'head' ? '#7dd3fc' : '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><ArrowUpFromLine size={14} /> Голова</button>
          </div>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Добавить стоек
            <input style={{ ...FIELD, marginTop: 4 }} type="number" min={1} max={1000} value={count} onChange={event => setCount(Math.max(1, Math.min(1000, Number(event.target.value) || 1)))} />
          </label>
          <div style={{ border: '1px solid #1f2937', padding: 10, fontSize: 11 }}>
            <div style={{ color: '#cbd5e1', fontWeight: 600, marginBottom: 6 }}>Новые адреса</div>
            <div style={{ color: '#60a5fa', fontFamily: 'monospace', wordBreak: 'break-word' }}>{plan.additions.map(rack => rack.label).join('  ')}</div>
            {plan.renumber.length > 0 && <>
              <div style={{ color: '#fbbf24', marginTop: 10, marginBottom: 4 }}>Изменение существующих адресов</div>
              {plan.renumber.map(item => <div key={item.rackId} style={{ color: '#cbd5e1', fontFamily: 'monospace' }}>{item.oldAddress} → {item.newAddress}</div>)}
            </>}
          </div>
          {plan.errors.length > 0 && <div role="alert" style={{ color: '#fca5a5', background: 'rgba(127,29,29,.2)', border: '1px solid rgba(248,113,113,.35)', padding: 9, fontSize: 11 }}>{plan.errors.map(error => <div key={error}>{error}</div>)}</div>}
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ ...FIELD, width: 'auto', cursor: 'pointer', color: '#cbd5e1' }}>Отмена</button>
          <button onClick={apply} disabled={!plan.canApply} style={{ ...FIELD, width: 'auto', cursor: plan.canApply ? 'pointer' : 'not-allowed', background: plan.canApply ? '#2563eb' : '#1f2937', borderColor: plan.canApply ? '#2563eb' : '#374151', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> Применить</button>
        </div>
      </div>
    </div>
  )
}
